import React, { ElementType, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Spinner,
  Switch,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Settings, MessageSquareText } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getInstagramConversations,
  getInstagramUserProfile,
  searchInstagramConversation,
  toggleInstagramAiChatResponses,
} from '../../../hooks/instagram';
import {
  instagramAccountDetails,
  InstagramConversation,
} from '../../../utils/types';
import {
  InstagramConversationRow,
  InstagramRefreshButton,
  InstagramSearchBar,
  InstagramSidebarEmpty,
  InstagramSidebarHeader,
  InstagramSidebarSkeleton,
  igAccent,
} from '../components/instagramInboxUi';

interface ChatSidebarProps {
  orgId: string;
  activeConversationId?: string;
}

const InstagramChatSidebar: React.FC<ChatSidebarProps> = ({
  orgId,
  activeConversationId,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [conversations, setConversations] = useState<InstagramConversation[]>([]);
  const [accountData, setAccountData] = useState<instagramAccountDetails | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);
  const aiInitialized = useRef(false);

  const loadInbox = async () => {
    setLoading(true);
    try {
      const [accountRes, convRes] = await Promise.all([
        getInstagramUserProfile(orgId),
        getInstagramConversations(orgId),
      ]);

      if (accountRes.success && Array.isArray(accountRes.data) && accountRes.data[0]) {
        setAccountData(accountRes.data[0]);
        setAiEnabled(Boolean(accountRes.data[0].ai_chat_responses));
        aiInitialized.current = true;
      } else {
        setAccountData(null);
      }

      if (convRes.success) {
        setConversations(Array.isArray(convRes.data) ? convRes.data : []);
      } else {
        throw new Error('Failed to load conversations');
      }
    } catch (error) {
      toast({
        title: 'Unable to load Instagram inbox',
        description: 'Check your connection and try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInbox();
  }, [orgId]);

  useEffect(() => {
    if (!aiInitialized.current || !accountData?.id) return;

    toggleInstagramAiChatResponses(orgId, accountData.id, aiEnabled).then((response) => {
      if (!response.success) {
        toast({
          title: 'Failed to update AI replies',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    });
  }, [aiEnabled]);

  const filteredConversations = useMemo(() => {
    if (!search.trim()) return conversations;
    const query = search.toLowerCase();
    return conversations.filter((conversation) => {
      const username = conversation.participant_username?.toLowerCase() || '';
      const snippet = conversation.snippet?.toLowerCase() || '';
      const lastText = conversation.last_message?.text?.toLowerCase() || '';
      return (
        username.includes(query) ||
        snippet.includes(query) ||
        lastText.includes(query)
      );
    });
  }, [conversations, search]);

  const handleSearchChange = async (value: string) => {
    setSearch(value);
    if (!value.trim()) {
      loadInbox();
      return;
    }

    setSearching(true);
    try {
      const response = await searchInstagramConversation(orgId, value.trim().toLowerCase());
      if (response.success && Array.isArray(response.data)) {
        setConversations(response.data);
      }
    } finally {
      setSearching(false);
    }
  };

  return (
    <Flex direction="column" h="100%">
      <InstagramSidebarHeader
        title="Instagram Inbox"
        subtitle={
          accountData?.username
            ? `Connected as @${accountData.username}`
            : 'Manage DMs and account activity'
        }
        actions={
          <>
            <InstagramRefreshButton onClick={loadInbox} />
            <Button
              variant="ghost"
              borderRadius="full"
              leftIcon={<Icon as={MessageSquareText as ElementType} boxSize={4} />}
              onClick={() =>
                accountData?.id &&
                navigate(`/organization/${orgId}/inbox/instagram/${accountData.id}/comments`)
              }
            >
              Comments
            </Button>
            <Button
              variant="ghost"
              borderRadius="full"
              leftIcon={<Icon as={Settings as ElementType} boxSize={4} />}
              onClick={() =>
                accountData?.id &&
                navigate(`/organization/${orgId}/inbox/instagram/${accountData.id}/settings`)
              }
            >
              Settings
            </Button>
          </>
        }
      />

      <InstagramSearchBar
        value={search}
        onChange={handleSearchChange}
        placeholder="Search people or messages"
      />

      <Box px={4} py={3} borderBottom="1px solid" borderColor="rgba(15, 23, 42, 0.08)">
        <Flex justify="space-between" align="center">
          <VStack align="flex-start" spacing={0}>
            <Text fontSize="sm" fontWeight="700" color="gray.800">
              Inbox controls
            </Text>
            <Text fontSize="xs" color="gray.500">
              Auto-reply for new incoming DMs
            </Text>
          </VStack>
          <HStack spacing={3}>
            <Badge colorScheme={aiEnabled ? 'purple' : 'gray'} borderRadius="full" px={2.5} py={0.5}>
              {aiEnabled ? 'AI on' : 'AI off'}
            </Badge>
            <Switch
              colorScheme="pink"
              isChecked={aiEnabled}
              onChange={(event) => setAiEnabled(event.target.checked)}
            />
          </HStack>
        </Flex>
      </Box>

      <Box flex="1" overflowY="auto">
        {loading ? <InstagramSidebarSkeleton /> : null}
        {!loading && searching ? (
          <Flex justify="center" py={6}>
            <Spinner color={igAccent} />
          </Flex>
        ) : null}
        {!loading && !filteredConversations.length ? (
          <InstagramSidebarEmpty
            title={search ? 'No matching conversations' : 'No conversations yet'}
            description={
              search
                ? 'Try a different name or clear search to view the full inbox.'
                : 'New Instagram conversations will appear here once messages start syncing.'
            }
          />
        ) : null}
        {!loading &&
          filteredConversations.map((conversation) => (
            <React.Fragment key={conversation.id}>
              <InstagramConversationRow
                conversation={conversation}
                active={activeConversationId === conversation.id}
                onClick={() =>
                  navigate(`/organization/${orgId}/inbox/instagram/conversations/${conversation.id}`)
                }
              />
              <Divider />
            </React.Fragment>
          ))}
      </Box>
    </Flex>
  );
};

export default InstagramChatSidebar;
